import React, { useState } from 'react';
import { FiCreditCard, FiTrash2, FiPlus } from 'react-icons/fi';
import './SavedCardSection.css';

/**
 * Capitalise the first letter of a card brand (e.g. "visa" → "Visa").
 */
const formatBrand = (brand) => {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

/**
 * SavedCardSection
 *
 * Shows the user's saved card with a "Remove" button, or an "Add payment
 * method" prompt if no card is saved yet.
 *
 * Props:
 *  - card: { id, brand, last4, expMonth, expYear } | null
 *  - onAddCard: () => void        – called when user wants to add a card
 *  - onRemoveCard: (id) => void   – called when user wants to remove a card
 *  - busy: boolean                – disable buttons while an action is pending
 */
const SavedCardSection = ({ card, onAddCard, onRemoveCard, busy }) => {
  const [confirmRemove, setConfirmRemove] = useState(false);

  const handleRemove = () => {
    if (!confirmRemove) {
      setConfirmRemove(true);
      return;
    }
    setConfirmRemove(false);
    onRemoveCard?.(card.id);
  };

  if (!card) {
    return (
      <div className="saved-card-section saved-card-empty">
        <div className="saved-card-empty-text">
          <FiCreditCard className="saved-card-icon" />
          <span>Add a payment method for 1-click purchases</span>
        </div>
        <button
          className="saved-card-add-btn"
          type="button"
          onClick={onAddCard}
          disabled={busy}
        >
          <FiPlus className="saved-card-btn-icon" />
          {busy ? 'Redirecting...' : 'Add card'}
        </button>
      </div>
    );
  }

  return (
    <div className="saved-card-section saved-card-filled">
      <div className="saved-card-info">
        <FiCreditCard className="saved-card-icon" />
        <span className="saved-card-label">
          {formatBrand(card.brand)} ending in <strong>{card.last4}</strong>
        </span>
        {card.expMonth && card.expYear && (
          <span className="saved-card-exp">
            {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
          </span>
        )}
      </div>
      <button
        className="saved-card-remove-btn"
        type="button"
        onClick={handleRemove}
        disabled={busy}
      >
        <FiTrash2 className="saved-card-btn-icon" />
        {confirmRemove ? 'Confirm remove' : 'Remove'}
      </button>
    </div>
  );
};

export default SavedCardSection;
